'use client';

import { useActionState, memo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Input from '@/components/common/Input';
import SubmitButton from '@/components/common/SubmitButton';
import GoogleOAuthButton from '@/components/common/GoogleOAuthButton';

type UserType = 'artist' | 'user' | 'admin';

interface LoginFormProps {
  userType: UserType;
  loginAction: (prevState: LoginResult | null, formData: FormData) => Promise<LoginResult>;
  signupLink?: string;
}

type LoginResult = {
  success: boolean;
  error?: string;
  fieldErrors?: {
    email?: string[];
    password?: string[];
  };
};

// URLパラメータのエラーコードを日本語メッセージにマッピング
const errorMessages: Record<string, string> = {
  no_token: 'トークンが見つかりません。',
  'Invalid token': 'トークンが無効です。もう一度サインアップしてください。',
  'Token already used': 'このトークンは既に使用されています。',
  'Token expired': 'トークンの有効期限が切れています（有効期限: 1時間）。もう一度サインアップしてください。',
  invalid_verification: '認証情報が無効です。',
  server_configuration: 'サーバー設定エラーが発生しました。しばらく待ってから再度お試しください。',
  session_creation_failed: 'セッションの作成に失敗しました。',
  verification_failed: '認証に失敗しました。もう一度お試しください。',
  access_denied: 'ログインがキャンセルされました。',
  session_invalid: 'セッションが無効です。再度ログインしてください。',
};

/**
 * ロール競合エラーメッセージを生成
 * error=role_conflict&existing_role=ユーザー の形式のURLパラメータに対応
 */
function getRoleConflictMessage(existingRole: string | null): string {
  if (existingRole) {
    return `このGoogleアカウントは既に「${existingRole}」として登録されています。別のGoogleアカウントをお使いください。`;
  }
  return 'このGoogleアカウントは既に別のロールで登録されています。別のGoogleアカウントをお使いください。';
}

const LoginFormContent = memo(({ userType, loginAction, signupLink }: LoginFormProps) => {
  const [state, formAction] = useActionState(loginAction, null);
  const [emailValue, setEmailValue] = useState('');
  const searchParams = useSearchParams();

  // URLパラメータからエラーを取得
  const urlError = searchParams.get('error');
  const urlErrorMessage = urlError
    ? urlError === 'role_conflict'
      ? getRoleConflictMessage(searchParams.get('existing_role'))
      : errorMessages[urlError] || 'エラーが発生しました。'
    : null;

  return (
    <div className="flex flex-col gap-10 w-full max-w-[343px] md:gap-10 md:w-80 md:max-w-none">
      <div className="text-center md:text-left">
        <h1 className="text-2xl font-bold text-mono-100 font-noto-sans-jp leading-[1.4] md:text-[28px]">
          ログイン
        </h1>
      </div>

      <form action={formAction} className="flex flex-col gap-6">
        <Input
          id="email"
          name="email"
          type="email"
          label="メールアドレス"
          placeholder="メールアドレスを入力してください"
          required
          error={state?.fieldErrors?.email?.[0]}
          autoComplete="email"
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
        />

        <Input
          id="password"
          name="password"
          type="password"
          label="パスワード"
          placeholder="パスワードを入力してください"
          required
          error={state?.fieldErrors?.password?.[0]}
          autoComplete="current-password"
          showPasswordToggle
        />

        {/* フォームアクションエラーを表示 */}
        {state?.error && (
          <div className="text-xs text-red-500 font-noto-sans-jp md:text-sm">
            {state.error}
          </div>
        )}

        {/* URLパラメータエラーを表示 */}
        {urlErrorMessage && (
          <div className="text-xs text-red-500 font-noto-sans-jp md:text-sm">
            {urlErrorMessage}
          </div>
        )}

        <SubmitButton>ログイン</SubmitButton>
      </form>

      {/* Google OAuth - adminは除外 */}
      {userType !== 'admin' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-mono-20" />
            <span className="text-sm text-mono-60 font-noto-sans-jp">または</span>
            <div className="flex-1 h-px bg-mono-20" />
          </div>
          <GoogleOAuthButton userType={userType} />
        </div>
      )}

      <div className="flex flex-col gap-6 items-center md:gap-6">
        <Link
          href="/auth/reset-password/request"
          className="text-base text-mono-80 font-roboto text-center underline hover:text-mono-100 transition-colors leading-[1.4] block md:text-base"
        >
          パスワードを忘れた方はこちら
        </Link>
        {signupLink && (
          <Link
            href={signupLink}
            className="text-base text-mono-80 font-roboto text-center underline hover:text-mono-100 transition-colors leading-[1.4] block md:text-base"
          >
            新規登録
          </Link>
        )}
      </div>
    </div>
  );
});

LoginFormContent.displayName = 'LoginFormContent';

export default LoginFormContent;
